import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
    Save, Plus, Trash2, ArrowUp, ArrowDown, 
    Settings, AlertCircle, GripVertical, Copy, RotateCcw,
    Eye, X, Star, Info
} from 'lucide-react';
import api from '../../../../api/client';
import { useTranslation } from 'react-i18next';
import CustomToastContainer from '../../../CustomToastContainer';
import { toast } from 'react-toastify';
import PageHeader, { PageCard, PageHeaderButton, PageShell } from '../../../common/PageHeader';
import { usePageLoading } from '../../../../contexts/LoadingContext';

function showQuestionnaireInsightsLink() {
    try {
        const raw = localStorage.getItem('user') || localStorage.getItem('adminUser');
        if (!raw) return false;
        const user = JSON.parse(raw);
        if (user?.is_superuser === true) return false;
        return Array.isArray(user?.admin_companies) && user.admin_companies.length > 0;
    } catch (err) {
        return false;
    }
}

const QUESTION_TYPES = [
    { value: 'text', label: 'Long Text' },
    { value: 'rating', label: 'Rating (1-5)' },
    { value: 'single_choice', label: 'Single Choice' },
    { value: 'multiple_choice', label: 'Multiple Choice' }
];

const FONT = { fontFamily: "'Poppins', sans-serif" };
const GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';

function ToggleSwitch({ checked, onClick, disabled, id }) {
    return (
        <button
            id={id}
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            onClick={onClick}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 ${
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            } ${checked ? 'bg-sky-600' : 'bg-gray-200'}`}
        >
            <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    checked ? 'translate-x-4' : 'translate-x-0'
                }`}
            />
        </button>
    );
}

function inputClass(hasError) {
    return `w-full rounded-lg border bg-white px-3 py-1.5 text-sm font-medium text-gray-900 placeholder:text-gray-400 transition-colors focus:outline-none ${
        hasError
            ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
            : 'border-gray-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20'
    }`;
}

const secondaryButtonClass =
    'inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:opacity-50';

function primaryButtonClass(disabled) {
    return `inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-200 ${
        disabled ? 'cursor-not-allowed bg-gray-400 shadow-none' : 'hover:opacity-95'
    }`;
}

function QuestionnaireChrome({ embedded, children }) {
    if (embedded) {
        return <div className="flex min-h-0 flex-1 flex-col" style={FONT}>{children}</div>;
    }
    return <PageShell className="font-sans" style={FONT}>{children}</PageShell>;
}

function QuestionnaireCard({ embedded, children }) {
    if (embedded) return children;
    return <PageCard>{children}</PageCard>;
}

export default function QuestionnaireSettings({ embedded = false }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    usePageLoading(!embedded && loading, { message: t('common.loading', 'Loading…') });
    const [saving, setSaving] = useState(false);
    const [questionnaire, setQuestionnaire] = useState(null);
    const [questions, setQuestions] = useState([]);
    
    // Original states for change tracking
    const [originalQuestionnaire, setOriginalQuestionnaire] = useState(null);
    const [originalQuestions, setOriginalQuestions] = useState([]);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewResponses, setPreviewResponses] = useState({});
    const [touched, setTouched] = useState(false);

    // Custom Confirmation Modal state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        confirmText: '',
        confirmColor: '',
        onConfirm: null
    });

    // Super Admin Switcher state
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('null');

    // Validation helper
    const getValidationErrors = () => {
        const errors = {};
        if (!questionnaire?.title || !questionnaire.title.trim()) {
            errors.title = "Title is required";
        }
        (questions || []).forEach((q) => {
            const qErrors = {};
            if (!q.question_text || !q.question_text.trim()) {
                qErrors.question_text = "Question text is required";
            }
            if (q.question_type === 'single_choice' || q.question_type === 'multiple_choice') {
                const choices = q.choices || [];
                if (choices.length < 2) {
                    qErrors.choices = "At least 2 choices are required";
                } else {
                    const emptyChoiceIdx = choices.findIndex(c => !c || !c.trim());
                    if (emptyChoiceIdx !== -1) {
                        qErrors.choices = "Choices cannot be empty";
                    }
                }
            }
            if (Object.keys(qErrors).length > 0) {
                errors[q.id] = qErrors;
            }
        });
        return errors;
    };

    const validationErrors = getValidationErrors();

    // Deep compare to track unsaved changes
    const hasUnsavedChanges = useMemo(() => {
        if (!questionnaire || !originalQuestionnaire) return false;

        // Compare general questionnaire settings
        if (questionnaire.title !== originalQuestionnaire.title) return true;
        if ((questionnaire.description || '') !== (originalQuestionnaire.description || '')) return true;
        if (questionnaire.is_enabled !== originalQuestionnaire.is_enabled) return true;
        if (questionnaire.is_optional !== originalQuestionnaire.is_optional) return true;

        // Compare questions
        if (questions.length !== originalQuestions.length) return true;

        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            const origQ = originalQuestions[i];

            if (!origQ) return true;
            if (q.id !== origQ.id) return true;
            if (q.question_text !== origQ.question_text) return true;
            if (q.question_type !== origQ.question_type) return true;
            if (q.display_order !== origQ.display_order) return true;
            if (q.isNew) return true;

            const qChoices = q.choices || [];
            const origChoices = origQ.choices || [];
            if (qChoices.length !== origChoices.length) return true;
            for (let j = 0; j < qChoices.length; j++) {
                if (qChoices[j] !== origChoices[j]) return true;
            }
        }

        return false;
    }, [questionnaire, originalQuestionnaire, questions, originalQuestions]);

    // Update global change tracking property
    useEffect(() => {
        window.hasUnsavedQuestionnaireChanges = hasUnsavedChanges;
        return () => {
            window.hasUnsavedQuestionnaireChanges = false;
        };
    }, [hasUnsavedChanges]);

    // Prevent browser refresh/tab close if there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = 'You have unsaved questionnaire changes. Are you sure you want to leave?';
                return 'You have unsaved questionnaire changes. Are you sure you want to leave?';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user'));
        const isSA = user?.is_superuser || user?.is_staff;
        setIsSuperAdmin(isSA);
        if (isSA) {
            fetchCompanies();
        }
        const defaultCompanyId = isSA ? 'null' : (user?.admin_companies?.[0] || 'null');
        setSelectedCompanyId(defaultCompanyId);
        fetchQuestionnaire(defaultCompanyId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCompanies = async () => {
        try {
            const response = await api.get('/companies/');
            const companyList = response.data?.results || response.data || [];
            setCompanies(companyList);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const fetchQuestionnaire = async (companyId) => {
        try {
            setLoading(true);
            const response = await api.get(`/feedback-questionnaires/?company=${companyId}`);
            const data = response.data?.results || response.data;
            if (data && data.length > 0) {
                const q = data[0];
                const qCopy = JSON.parse(JSON.stringify(q));
                setQuestionnaire(qCopy);
                setOriginalQuestionnaire(JSON.parse(JSON.stringify(qCopy)));
                setQuestions(qCopy.questions || []);
                setOriginalQuestions(JSON.parse(JSON.stringify(qCopy.questions || [])));
            } else {
                // If none exists, create a default one
                await createDefaultQuestionnaire(companyId);
            }
        } catch (error) {
            console.error('Error fetching questionnaire:', error);
            toast.error('Failed to load questionnaire settings');
        } finally {
            setLoading(false);
        }
    };

    const createDefaultQuestionnaire = async (companyId) => {
        try {
            const actualCompanyId = companyId === 'null' ? null : companyId;
            const response = await api.post('/feedback-questionnaires/', {
                company: actualCompanyId,
                title: 'Post-Interview Feedback',
                description: actualCompanyId 
                    ? 'We value your feedback. Please let us know how your interview experience was.' 
                    : "We'd love to hear your thoughts about your practice interview experience.",
                is_enabled: true,
                is_optional: true
            });
            const q = response.data;
            const qCopy = JSON.parse(JSON.stringify(q));
            setQuestionnaire(qCopy);
            setOriginalQuestionnaire(JSON.parse(JSON.stringify(qCopy)));
            setQuestions([]);
            setOriginalQuestions([]);
        } catch (error) {
            console.error('Error creating default questionnaire:', error);
        }
    };

    const handleCompanyChange = (companyId) => {
        setSelectedCompanyId(companyId);
        fetchQuestionnaire(companyId);
    };

    const handleQuestionnaireChange = (field, value) => {
        setQuestionnaire(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        const errors = getValidationErrors();
        if (Object.keys(errors).length > 0) {
            setTouched(true);
            toast.error('Please fix validation errors before saving');
            return;
        }

        try {
            setSaving(true);
            
            // 1. Save general questionnaire settings
            await api.put(`/feedback-questionnaires/${questionnaire.id}/`, {
                title: questionnaire.title,
                description: questionnaire.description,
                is_enabled: questionnaire.is_enabled,
                is_optional: questionnaire.is_optional,
                company: questionnaire.company
            });

            // 2. Save all questions (both new and updated)
            for (const q of questions) {
                if (q.isNew) {
                    const { id, isNew, ...data } = q;
                    await api.post(`/feedback-questionnaires/${questionnaire.id}/questions/`, data);
                } else {
                    await api.put(`/feedback-questionnaires/${questionnaire.id}/questions/${q.id}/`, q);
                }
            }

            toast.success('Changes saved successfully');
            setTouched(false);
            await fetchQuestionnaire(selectedCompanyId); // Refresh to get real database state and IDs
        } catch (error) {
            console.error('Error saving changes:', error);
            toast.error('Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    const addQuestion = () => {
        if (questions.length >= 5) {
            toast.warning('You can add a maximum of 5 questions');
            return;
        }
        setQuestions([...questions, {
            id: `temp_${Date.now()}`,
            question_text: '',
            question_type: 'text',
            choices: [],
            display_order: questions.length,
            isNew: true
        }]);
    };

    const duplicateQuestion = (index) => {
        if (questions.length >= 5) {
            toast.warning('You can add a maximum of 5 questions');
            return;
        }
        const qToDup = questions[index];
        const dupQuestion = {
            id: `temp_${Date.now()}`,
            question_text: qToDup.question_text ? `${qToDup.question_text} (Copy)` : '',
            question_type: qToDup.question_type,
            choices: [...(qToDup.choices || [])],
            display_order: questions.length,
            isNew: true
        };
        setQuestions([...questions, dupQuestion]);
        toast.info('Question duplicated. Click Save Changes to store.');
    };

    const removeQuestion = async (index) => {
        const question = questions[index];
        if (!question.isNew) {
            try {
                await api.delete(`/feedback-questionnaires/${questionnaire.id}/questions/${question.id}/`);
            } catch (error) {
                console.error('Error deleting question:', error);
                toast.error('Failed to delete question');
                return;
            }
        }
        
        const newQuestions = [...questions];
        newQuestions.splice(index, 1);
        
        // Update display_order for remaining questions
        const updatedQuestions = newQuestions.map((q, i) => ({ ...q, display_order: i }));
        setQuestions(updatedQuestions);
        
        // Save the new order if not new
        if (!question.isNew) {
            setSaving(true);
            try {
                for (const q of updatedQuestions) {
                    await api.put(`/feedback-questionnaires/${questionnaire.id}/questions/${q.id}/`, q);
                }
                toast.success('Question removed');
                setOriginalQuestions(JSON.parse(JSON.stringify(updatedQuestions)));
            } catch (err) {
                toast.error('Failed to update question ordering');
            } finally {
                setSaving(false);
            }
        }
    };

    const moveQuestion = (index, direction) => {
        if ((direction === -1 && index === 0) || (direction === 1 && index === questions.length - 1)) return;
        
        const newQuestions = [...questions];
        const temp = newQuestions[index];
        newQuestions[index] = newQuestions[index + direction];
        newQuestions[index + direction] = temp;
        
        // Update display_order
        const updatedQuestions = newQuestions.map((q, i) => ({ ...q, display_order: i }));
        setQuestions(updatedQuestions);
    };

    const updateQuestion = (index, field, value) => {
        const newQuestions = [...questions];
        newQuestions[index][field] = value;
        if (field === 'question_type' && (value === 'single_choice' || value === 'multiple_choice')) {
            if (!newQuestions[index].choices || newQuestions[index].choices.length === 0) {
                newQuestions[index].choices = ['', ''];
            }
        }
        setQuestions(newQuestions);
    };

    const handleDiscardChanges = () => {
        setConfirmModal({
            isOpen: true,
            title: 'Discard Changes Confirmation',
            message: 'Are you sure you want to discard all unsaved changes?',
            confirmText: 'Discard',
            confirmColor: 'bg-red-600 hover:bg-red-700 text-white',
            onConfirm: () => {
                setQuestionnaire(JSON.parse(JSON.stringify(originalQuestionnaire)));
                setQuestions(JSON.parse(JSON.stringify(originalQuestions)));
                setTouched(false);
                toast.info('Changes discarded');
            }
        });
    };

    const handleRestoreDefaults = async () => {
        setConfirmModal({
            isOpen: true,
            title: 'Use Default Template',
            message: 'Are you sure you want to restore the recommended feedback questions? This will delete your current custom questions.',
            confirmText: 'Restore',
            confirmColor: 'bg-red-600 hover:bg-red-700 text-white',
            onConfirm: async () => {
                try {
                    setSaving(true);
                    const response = await api.post(`/feedback-questionnaires/${questionnaire.id}/restore-defaults/`);
                    const restoredQuestions = response.data || [];
                    setQuestions(restoredQuestions);
                    setOriginalQuestions(JSON.parse(JSON.stringify(restoredQuestions)));
                    toast.success('Recommended questions restored successfully');
                } catch (error) {
                    console.error('Error restoring recommended questions:', error);
                    toast.error('Failed to restore recommended questions');
                } finally {
                    setSaving(false);
                }
            }
        });
    };

    if (loading || !questionnaire) {
        if (embedded) {
            return (
                <div className="p-4 sm:p-5 text-sm text-gray-500" style={FONT}>
                    {t('common.loading', 'Loading…')}
                </div>
            );
        }
        return null;
    }

    const restoreTooltip = (
        <div className="absolute top-full left-1/2 z-[999] mt-2 hidden w-72 -translate-x-1/2 pointer-events-none rounded-lg border border-gray-200 bg-white p-3 text-left text-[11px] font-medium leading-relaxed text-gray-700 shadow-xl transition-all duration-200 group-hover:block">
            <p className="mb-1 font-semibold text-gray-900">Restore AptiView's recommended questionnaire.</p>
            <ul className="list-none space-y-0.5 pl-0 text-gray-500">
                <li>• Includes industry-standard feedback questions</li>
                <li>• Designed using hiring best practices</li>
                <li>• Restoring will replace your current questionnaire</li>
            </ul>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-white"></div>
        </div>
    );

    const restoreAndSaveActions = embedded ? (
        <>
            <div className="relative group">
                <button
                    type="button"
                    onClick={handleRestoreDefaults}
                    disabled={saving}
                    className={secondaryButtonClass}
                >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Use Default Template
                    <Info className="h-3.5 w-3.5 shrink-0 opacity-70" />
                </button>
                {restoreTooltip}
            </div>
            <button
                type="button"
                onClick={handleSaveChanges}
                disabled={saving || !hasUnsavedChanges}
                className={primaryButtonClass(saving || !hasUnsavedChanges)}
                style={{ background: saving || !hasUnsavedChanges ? undefined : GRADIENT }}
            >
                <Save size={16} aria-hidden />
                {saving ? 'Saving...' : 'Save Changes'}
            </button>
        </>
    ) : (
        <>
            <div className="relative group">
                <PageHeaderButton
                    onClick={handleRestoreDefaults}
                    disabled={saving}
                    icon={<RotateCcw className="h-3.5 w-3.5" />}
                >
                    Use Default Template
                    <Info className="ml-0.5 h-3.5 w-3.5 shrink-0 opacity-80" />
                </PageHeaderButton>
                {restoreTooltip}
            </div>
            <PageHeaderButton
                onClick={handleSaveChanges}
                disabled={saving || !hasUnsavedChanges}
                color="indigo"
                icon={<Save className="h-3.5 w-3.5" />}
            >
                {saving ? 'Saving...' : 'Save Changes'}
            </PageHeaderButton>
        </>
    );

    return (
        <QuestionnaireChrome embedded={embedded}>
            <CustomToastContainer />
            <QuestionnaireCard embedded={embedded}>
                {embedded ? null : (
                    <PageHeader
                        title="Post-Interview Feedback"
                        subtitle="Configure the feedback questionnaire shown to candidates after they complete an interview."
                        extra={
                            showQuestionnaireInsightsLink() ? (
                                <Link
                                    to="/feedback-insights"
                                    className="mt-2 inline-flex text-xs font-semibold text-white underline underline-offset-2 hover:text-blue-50"
                                >
                                    View insights
                                </Link>
                            ) : null
                        }
                    >
                        {restoreAndSaveActions}
                    </PageHeader>
                )}

                <div className="p-4 sm:p-5">
                    <div className="space-y-4">
                    {embedded && (
                        <div className="flex flex-wrap items-center gap-2">
                            {showQuestionnaireInsightsLink() ? (
                                <Link
                                    to="/feedback-insights"
                                    className="text-xs font-semibold text-sky-700 underline underline-offset-2 hover:text-sky-800"
                                >
                                    View insights
                                </Link>
                            ) : null}
                            <div className="ml-auto flex flex-wrap items-center gap-2">
                                {restoreAndSaveActions}
                            </div>
                        </div>
                    )}

                    {isSuperAdmin && (
                        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-4">
                            <label className="block text-sm font-semibold text-gray-900 mb-1">
                                Questionnaire Scope (Super Admin)
                            </label>
                            <p className="mb-2.5 text-xs leading-snug text-gray-500">
                                Choose which company this questionnaire applies to.
                            </p>
                            <select
                                value={selectedCompanyId}
                                onChange={(e) => handleCompanyChange(e.target.value)}
                                className={`${inputClass(false)} h-9 max-w-md`}
                            >
                                <option value="null">Global (B2C Practice Interviews)</option>
                                {companies.map(c => (
                                    <option key={c.id} value={c.id}>
                                        Company: {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500">
                                <Settings className="h-4 w-4" />
                            </div>
                            <h2 className="text-sm font-semibold text-gray-900">General Settings</h2>
                        </div>

                        <div className="divide-y divide-gray-100">
                            <div className="flex items-center gap-3 px-4 py-3">
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-gray-900">Enable Questionnaire</p>
                                    <p className="mt-0.5 text-xs leading-snug text-gray-500">
                                        Show this questionnaire to candidates after interviews
                                    </p>
                                </div>
                                <ToggleSwitch
                                    checked={questionnaire.is_enabled}
                                    onClick={() => handleQuestionnaireChange('is_enabled', !questionnaire.is_enabled)}
                                />
                            </div>
                            <div className="flex items-center gap-3 px-4 py-3">
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-gray-900">Optional Questionnaire</p>
                                    <p className="mt-0.5 text-xs leading-snug text-gray-500">
                                        Allow candidates to skip without answering
                                    </p>
                                </div>
                                <ToggleSwitch
                                    checked={questionnaire.is_optional}
                                    onClick={() => handleQuestionnaireChange('is_optional', !questionnaire.is_optional)}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 sm:divide-x divide-y sm:divide-y-0 divide-gray-100 border-t border-gray-100">
                            <div className="p-4">
                                <label className="block text-sm font-semibold text-gray-900">Title</label>
                                <p className="mt-1 text-xs leading-snug text-gray-500">Shown to candidates at the top of the questionnaire.</p>
                                <input
                                    type="text"
                                    value={questionnaire.title}
                                    onChange={(e) => handleQuestionnaireChange('title', e.target.value)}
                                    className={`${inputClass(touched && validationErrors.title)} h-9 mt-2.5`}
                                    placeholder="e.g. Post-Interview Feedback"
                                />
                                {touched && validationErrors.title && (
                                    <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                        {validationErrors.title}
                                    </p>
                                )}
                            </div>
                            <div className="p-4">
                                <label className="block text-sm font-semibold text-gray-900">Description (Optional)</label>
                                <p className="mt-1 text-xs leading-snug text-gray-500">Message shown before the questions.</p>
                                <textarea
                                    value={questionnaire.description || ''}
                                    onChange={(e) => handleQuestionnaireChange('description', e.target.value)}
                                    className={`${inputClass(false)} mt-2.5 min-h-[72px]`}
                                    placeholder="Message shown before the questions..."
                                />
                            </div>
                        </div>
                    </div>

                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
                            <h2 className="text-sm font-semibold text-gray-900">Questions ({questions.length})</h2>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setPreviewResponses({});
                                        setIsPreviewOpen(true);
                                    }}
                                    className={secondaryButtonClass}
                                >
                                    <Eye className="h-3.5 w-3.5 text-gray-500" />
                                    Preview
                                </button>
                                <button
                                    type="button"
                                    onClick={addQuestion}
                                    disabled={questions.length >= 5}
                                    className={
                                        questions.length >= 5
                                            ? secondaryButtonClass
                                            : 'inline-flex items-center justify-center gap-1.5 rounded-lg border border-sky-200 bg-white px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50 transition-colors'
                                    }
                                    title={questions.length >= 5 ? 'Maximum of 5 questions allowed' : ''}
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                    Add Question
                                </button>
                            </div>
                        </div>

                        <div className="p-4 space-y-3">
                            {questions.length === 0 ? (
                                <div className="rounded-lg border border-dashed border-gray-200 py-10 text-center text-sm text-gray-400">
                                    No questions added yet. Click "Add Question" to start.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {questions.map((q, index) => (
                                        <div key={q.id} className="rounded-lg border border-gray-200 bg-white">
                                            <div className="flex flex-wrap items-center justify-between gap-3 p-3">
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <div className="text-gray-400 p-1 rounded">
                                                        <GripVertical className="h-4 w-4" />
                                                    </div>
                                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-sm font-semibold text-sky-700">
                                                        {index + 1}
                                                    </div>
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <input
                                                        type="text"
                                                        value={q.question_text}
                                                        onChange={(e) => updateQuestion(index, 'question_text', e.target.value)}
                                                        className={`${inputClass(touched && validationErrors[q.id]?.question_text)} h-9`}
                                                        placeholder="Enter question text..."
                                                    />
                                                    {touched && validationErrors[q.id]?.question_text && (
                                                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                                                            <AlertCircle className="h-3 w-3" />
                                                            {validationErrors[q.id].question_text}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-2 shrink-0">
                                                    <select
                                                        value={q.question_type}
                                                        onChange={(e) => updateQuestion(index, 'question_type', e.target.value)}
                                                        className="h-9 rounded-lg border border-gray-200 bg-white px-2 text-xs font-semibold text-gray-600 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20"
                                                    >
                                                        {QUESTION_TYPES.map(type => (
                                                            <option key={type.value} value={type.value}>{type.label}</option>
                                                        ))}
                                                    </select>

                                                    <div className="flex items-center gap-0.5">
                                                        <button
                                                            type="button"
                                                            onClick={() => moveQuestion(index, -1)}
                                                            disabled={index === 0}
                                                            className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
                                                        >
                                                            <ArrowUp className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => moveQuestion(index, 1)}
                                                            disabled={index === questions.length - 1}
                                                            className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:text-gray-400 transition-colors"
                                                        >
                                                            <ArrowDown className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>

                                                    <div className="w-px h-5 bg-gray-200"></div>

                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => duplicateQuestion(index)}
                                                            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-50 transition-all"
                                                            title="Duplicate Question"
                                                        >
                                                            <Copy className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeQuestion(index)}
                                                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-all"
                                                            title="Remove Question"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {(q.question_type === 'single_choice' || q.question_type === 'multiple_choice') && (
                                                <div className="px-3 pb-3">
                                                    <div className={`rounded-lg border bg-slate-50/70 p-3 ${
                                                        touched && validationErrors[q.id]?.choices ? 'border-red-300 bg-red-50/10' : 'border-gray-200'
                                                    }`}>
                                                        <label className="block text-xs font-semibold text-gray-500 mb-2">
                                                            Choices / Options (Minimum 2 required)
                                                        </label>
                                                        <div className="space-y-2">
                                                            {(q.choices || []).map((choice, choiceIndex) => (
                                                                <div key={choiceIndex} className="flex items-center gap-2">
                                                                    <span className="w-5 text-right text-xs font-semibold text-gray-400">
                                                                        {choiceIndex + 1}.
                                                                    </span>
                                                                    <input
                                                                        type="text"
                                                                        value={choice}
                                                                        onChange={(e) => {
                                                                            const newChoices = [...q.choices];
                                                                            newChoices[choiceIndex] = e.target.value;
                                                                            updateQuestion(index, 'choices', newChoices);
                                                                        }}
                                                                        className={`${inputClass(false)} h-9 flex-1`}
                                                                        placeholder={`Option ${choiceIndex + 1}`}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            const newChoices = (q.choices || []).filter((_, i) => i !== choiceIndex);
                                                                            updateQuestion(index, 'choices', newChoices);
                                                                        }}
                                                                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-all shrink-0"
                                                                        title="Delete Option"
                                                                    >
                                                                        <Trash2 className="h-3.5 w-3.5" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-200/60">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newChoices = [...(q.choices || []), ''];
                                                                    updateQuestion(index, 'choices', newChoices);
                                                                }}
                                                                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50 transition-colors"
                                                            >
                                                                <Plus className="h-3.5 w-3.5" />
                                                                Add Option
                                                            </button>
                                                            {touched && validationErrors[q.id]?.choices && (
                                                                <span className="text-xs text-red-600 flex items-center gap-1">
                                                                    <AlertCircle className="h-3.5 w-3.5" />
                                                                    {validationErrors[q.id].choices}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {questions.length < 5 ? (
                                        <div className="mt-1 flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-sky-200 bg-sky-50/40 px-4 py-4">
                                            <button
                                                type="button"
                                                onClick={addQuestion}
                                                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-sky-200 bg-white px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-50 transition-colors"
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                                Add Another Question
                                            </button>
                                            <p className="text-xs text-gray-500">
                                                Add more questions to continue building your questionnaire. ({questions.length}/5)
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="mt-1 flex flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-gray-200 bg-gray-50/50 px-4 py-4">
                                            <span className="text-xs font-semibold text-gray-400">Maximum of 5 questions reached</span>
                                            <p className="text-[11px] text-gray-400">Remove a question to add a new one.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    </div>
                </div>

                {hasUnsavedChanges && (
                    <div className="sticky bottom-0 z-20 border-t border-amber-200 bg-amber-50/95 backdrop-blur-sm px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 animate-slide-up">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="bg-amber-100 p-2 rounded-lg text-amber-600 shrink-0">
                                <AlertCircle className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                                <h4 className="text-sm font-semibold text-gray-900">Unsaved Changes</h4>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    You have modified the questionnaire configuration. Save or discard your changes before leaving.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setPreviewResponses({});
                                    setIsPreviewOpen(true);
                                }}
                                className={secondaryButtonClass}
                            >
                                <Eye className="h-3.5 w-3.5" />
                                Preview
                            </button>
                            <button
                                type="button"
                                onClick={handleDiscardChanges}
                                disabled={saving}
                                className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-60"
                            >
                                Discard Changes
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveChanges}
                                disabled={saving}
                                className={primaryButtonClass(saving)}
                                style={{ background: saving ? undefined : GRADIENT }}
                            >
                                <Save size={16} aria-hidden />
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                )}
            </QuestionnaireCard>

            {/* Preview Modal */}
            {isPreviewOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
                    <div
                        className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col my-8 animate-in fade-in zoom-in duration-200"
                        style={{ maxHeight: '90vh' }}
                    >
                        {/* Modal Header */}
                        <div className="px-4 py-2.5 flex items-center justify-between shrink-0 text-white" style={{ background: GRADIENT }}>
                            <div className="flex items-center gap-3">
                                <div className="bg-white/20 p-1.5 rounded-lg">
                                    <Eye className="h-4 w-4 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-semibold leading-tight text-white">Questionnaire Preview</h2>
                                    <p className="text-xs text-blue-100">This is how candidates will view the questionnaire.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsPreviewOpen(false)}
                                className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 overflow-y-auto flex-1">
                            {/* Mock candidate interface container */}
                            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm max-w-xl mx-auto my-2">
                                <div className="border-b border-gray-150 pb-4 mb-6">
                                    <h2 className="text-xl font-bold text-gray-900">{questionnaire.title || "Feedback Questionnaire"}</h2>
                                    {questionnaire.description && (
                                        <p className="text-gray-600 text-xs mt-2 leading-relaxed">{questionnaire.description}</p>
                                    )}
                                </div>

                                <div className="space-y-6">
                                    {questions.length === 0 ? (
                                        <p className="text-sm text-gray-400 italic text-center py-4">No questions added yet.</p>
                                    ) : (
                                        [...questions]
                                            .sort((a, b) => a.display_order - b.display_order)
                                            .map((q, idx) => {
                                                const val = previewResponses[q.id];
                                                return (
                                                    <div key={q.id || idx} className="space-y-3">
                                                        <label className="block font-semibold text-gray-800 text-sm">
                                                            {idx + 1}. {q.question_text || <span className="text-red-400 italic">(Empty Question Text)</span>}
                                                        </label>

                                                        {/* Rating Input */}
                                                        {q.question_type === "rating" && (
                                                            <div className="flex gap-2 items-center">
                                                                {[1, 2, 3, 4, 5].map((star) => (
                                                                    <button
                                                                        key={star}
                                                                        type="button"
                                                                        onClick={() => setPreviewResponses(prev => ({ ...prev, [q.id]: star }))}
                                                                        className="focus:outline-none transition transform hover:scale-110"
                                                                    >
                                                                        <Star
                                                                            className={`h-6 w-6 ${
                                                                                star <= (val || 0)
                                                                                    ? "text-yellow-400 fill-yellow-400"
                                                                                    : "text-gray-300 hover:text-yellow-300"
                                                                            }`}
                                                                        />
                                                                    </button>
                                                                ))}
                                                                <span className="text-gray-500 text-xs font-semibold ml-2">
                                                                    {val ? `${val} / 5` : ""}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Text Input */}
                                                        {q.question_type === "text" && (
                                                            <textarea
                                                                rows={3}
                                                                value={val || ""}
                                                                onChange={(e) => setPreviewResponses(prev => ({ ...prev, [q.id]: e.target.value }))}
                                                                className="w-full rounded-xl border border-gray-300 p-3 text-xs text-gray-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none transition-all"
                                                                placeholder="Type feedback response here..."
                                                            />
                                                        )}

                                                        {/* Single Choice Input */}
                                                        {q.question_type === "single_choice" && (
                                                            <div className="grid gap-2">
                                                                {(!q.choices || q.choices.length === 0) ? (
                                                                    <p className="text-xs text-gray-400 italic">No choices configured.</p>
                                                                ) : (
                                                                    q.choices.map((choice, cIdx) => (
                                                                        <label
                                                                            key={cIdx}
                                                                            className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition ${
                                                                                val === choice
                                                                                    ? "bg-indigo-50/50 border-indigo-300 text-indigo-900"
                                                                                    : "bg-gray-50/50 border-gray-250 text-gray-700 hover:bg-gray-50"
                                                                            }`}
                                                                        >
                                                                            <input
                                                                                type="radio"
                                                                                name={`preview-q-${q.id}`}
                                                                                value={choice}
                                                                                checked={val === choice}
                                                                                onChange={() => setPreviewResponses(prev => ({ ...prev, [q.id]: choice }))}
                                                                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                                                                            />
                                                                            <span className="text-xs font-medium">{choice || <span className="text-red-400 italic">(Empty option)</span>}</span>
                                                                        </label>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Multiple Choice Input */}
                                                        {q.question_type === "multiple_choice" && (
                                                            <div className="grid gap-2">
                                                                {(!q.choices || q.choices.length === 0) ? (
                                                                    <p className="text-xs text-gray-400 italic">No choices configured.</p>
                                                                ) : (
                                                                    q.choices.map((choice, cIdx) => {
                                                                        const isChecked = (val || []).includes(choice);
                                                                        return (
                                                                            <label
                                                                                key={cIdx}
                                                                                className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition ${
                                                                                    isChecked
                                                                                        ? "bg-indigo-50/50 border-indigo-300 text-indigo-900"
                                                                                        : "bg-gray-50/50 border-gray-250 text-gray-700 hover:bg-gray-50"
                                                                                }`}
                                                                            >
                                                                                <input
                                                                                    type="checkbox"
                                                                                    value={choice}
                                                                                    checked={isChecked}
                                                                                    onChange={(e) => {
                                                                                        const currentList = val || [];
                                                                                        const updatedList = e.target.checked
                                                                                            ? [...currentList, choice]
                                                                                            : currentList.filter(item => item !== choice);
                                                                                        setPreviewResponses(prev => ({ ...prev, [q.id]: updatedList }));
                                                                                    }}
                                                                                    className="h-4 w-4 text-indigo-600 rounded focus:ring-indigo-500"
                                                                                />
                                                                                <span className="text-xs font-medium">{choice || <span className="text-red-400 italic">(Empty option)</span>}</span>
                                                                            </label>
                                                                        );
                                                                    })
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                    )}
                                </div>

                                <div className="flex gap-4 border-t border-gray-150 pt-5 mt-6">
                                    {questionnaire.is_optional && (
                                        <button
                                            type="button"
                                            className="flex-1 border border-gray-300 text-gray-700 rounded-xl px-4 py-2.5 font-semibold text-xs transition bg-white hover:bg-gray-50 shadow-sm"
                                            onClick={() => { toast.info("Skip button clicked (Preview Mode)"); setIsPreviewOpen(false); }}
                                        >
                                            Skip Feedback
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="flex-1 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:opacity-95"
                                        style={{ background: GRADIENT }}
                                        onClick={() => { toast.info("Submit button clicked (Preview Mode)"); setIsPreviewOpen(false); }}
                                    >
                                        Submit Feedback
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="bg-gray-100 px-6 py-3 flex justify-end shrink-0 border-t border-gray-200">
                            <button
                                onClick={() => setIsPreviewOpen(false)}
                                className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Confirmation Modal */}
            {confirmModal.isOpen && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[500px] overflow-hidden flex flex-col border border-gray-250 animate-slide-up">
                        {/* Modal Header */}
                        <div className="relative px-6 py-4 flex items-center justify-center border-b border-gray-150">
                            <h3 className="text-lg font-bold text-gray-900 text-center">{confirmModal.title}</h3>
                            <button
                                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                                className="absolute right-4 text-gray-400 hover:text-gray-600 rounded-full p-1.5 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body & Footer combined */}
                        <div className="px-8 py-6 flex flex-col items-center justify-center gap-6">
                            <p className="text-sm text-gray-600 text-center leading-relaxed">
                                {confirmModal.message}
                            </p>
                            
                            <div className="flex items-center justify-center gap-4 w-full">
                                <button
                                    type="button"
                                    onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                                    className="px-6 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-xl text-xs font-semibold transition-all shadow-sm text-center"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (confirmModal.onConfirm) confirmModal.onConfirm();
                                        setConfirmModal(prev => ({ ...prev, isOpen: false }));
                                    }}
                                    className={`px-6 py-2 text-white rounded-xl text-xs font-semibold transition-all shadow-md text-center ${confirmModal.confirmColor}`}
                                >
                                    {confirmModal.confirmText}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Styles */}
            <style>{`
              @keyframes slideUp {
                from {
                  transform: translateY(20px);
                  opacity: 0;
                }
                to {
                  transform: translateY(0);
                  opacity: 1;
                }
              }
              .animate-slide-up {
                animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              }
            `}</style>
        </QuestionnaireChrome>
    );
}
